import { type CSSProperties } from 'react';
import { useNavigate } from 'react-router-dom';
import { useRxDocument, useRxPatchMutation } from '@/hooks/rxdb-base';
import type { Block, Instruction, Note, UserSource } from '@flintmint/core-shared/rxdb';

import { useElementResize } from '@flintmint/web-shared/hooks/use-element-resize';
import { useLeaveOnArchived } from '@/hooks/use-leave-on-archived';

import { Button } from '@flintmint/web-shared/components/button';
import { PreviewEditorHolder } from '@/components/preview-editor-holder';
import { SimpleDocumentHeader } from '@/components/simple-document-header';

import { appRoutes } from '@/constants/routes';
import { toRem } from '@flintmint/web-shared/utils/ui';

import styles from './document-preview.module.scss';

export const DocumentPreview = (props: DocumentPreviewProps) => {
  const { onClose, docId, type } = props;

  const navigate = useNavigate();

  const collection = type === 'templates' ? 'notes' : type;
  const scrollId = 'document-preview-scroll-id';

  const { data: document, isLoading } = useRxDocument<Note | Block | UserSource | Instruction>(collection!, docId);
  const { mutate: patchDocument } = useRxPatchMutation<Note | Block | UserSource | Instruction>();

  const { ref, clientHeight } = useElementResize([isLoading, !document]);

  useLeaveOnArchived<Note | Block | UserSource | Instruction>(document, onClose);

  if (isLoading || !document) {
    return null;
  }

  const navigateToEditor = () => {
    switch (type) {
      case 'userSources':
        return navigate(`${appRoutes.cortex}/${document.id}`);
      case 'templates':
      case 'notes':
        return navigate(`${appRoutes.library}/${document.id}`);
      case 'blocks':
        return navigate(`${appRoutes.library}`, { state: { blockId: document.id } });
      default:
        return null;
    }
  };

  return (
    <div
      ref={ref}
      id={scrollId}
      className={styles.documentPreview}
      style={
        {
          ['--scroll-height']: toRem(clientHeight),
        } as CSSProperties
      }
    >
      <SimpleDocumentHeader
        type={type}
        data={document}
        onClose={onClose}
        documentName={document.name}
        onTagsUpdate={(tagIds) => patchDocument(document, { tagIds })}
        className={styles.previewHeader}
        actions={
          <Button color="dark" appearance="outline" onClick={navigateToEditor}>
            Open in Editor
          </Button>
        }
      />
      <PreviewEditorHolder
        className={styles.editorHolder}
        editorProps={{
          onUpdate: () => {},
          editorType: collection!,
          scrollParentId: scrollId,
          placeholder: `No content yet`,
          content: document.content,
        }}
      />
    </div>
  );
};

export interface DocumentPreviewProps {
  type?: string;
  docId?: string;
  onClose: () => void;
}
